'use client';

/**
 * A place's picture, never an empty box. Underneath every photo sits a flat
 * tile for the kind of place (a tent on green for a camp, a caravan on sand
 * for a caravan park, a bed on violet for a motel, hills on blue for a town
 * or park). The photo fades in over it once it has really loaded; a
 * dead link, or a 1px tracking pixel, leaves the drawn tile showing.
 */
import React from 'react';
import { IconCaravan, IconLandscape, IconTent } from './icons';

export type PlaceKind = 'town' | 'camp' | 'caravan' | 'stay' | 'nature';

/**
 * Each kind is one flat, category-coloured tile with its glyph: the same
 * size and corner radius as a photo, so a column of mixed photos and tiles
 * reads as deliberate, not as a picture that failed to load. The colours
 * are the map's: green for camps, sand for caravan parks, violet for
 * stays, blue for towns, teal for nature.
 */
const TILE: Record<PlaceKind, { bg: string; fg: string; ring: string }> = {
  town: { bg: '#e6eef9', fg: '#2f5d93', ring: 'rgba(47,93,147,0.14)' },
  nature: { bg: '#e2f1ea', fg: '#2f6b4b', ring: 'rgba(47,107,75,0.14)' },
  camp: { bg: '#e6f0df', fg: '#3f6a2a', ring: 'rgba(63,106,42,0.14)' },
  caravan: { bg: '#f7ecda', fg: '#7a5222', ring: 'rgba(122,82,34,0.14)' },
  stay: { bg: '#ebe8f7', fg: '#4a4296', ring: 'rgba(74,66,150,0.14)' },
};

/** What kind of place a label describes ("Caravan park · Cann River"). */
export function placeKindFrom(text: string | null | undefined): PlaceKind {
  const t = (text || '').toLowerCase();
  if (/caravan|holiday park|tourist park/.test(t)) return 'caravan';
  if (/cabin|motel|hotel|apartment|lodge|b&b|bed and breakfast|stay|accommodation/.test(t)) return 'stay';
  if (/camp|rest area|free_camping|bush/.test(t)) return 'camp';
  if (/national park|state forest|reserve|falls|beach|lookout|walk|nature|park/.test(t)) return 'nature';
  return 'town';
}

function BedIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M3 18V7M3 14h18v4M21 14v-2.5A2.5 2.5 0 0 0 18.5 9H11v5" />
      <circle cx="7" cy="11" r="1.6" />
    </svg>
  );
}

export function PlaceTileArt({ kind, iconClassName = 'h-5 w-5' }: { kind: PlaceKind; iconClassName?: string }) {
  const c = TILE[kind];
  const Glyph = kind === 'camp' ? IconTent : kind === 'caravan' ? IconCaravan : kind === 'stay' ? BedIcon : IconLandscape;
  return (
    <span
      aria-hidden="true"
      className="absolute inset-0 flex items-center justify-center"
      style={{ color: c.fg, background: c.bg, boxShadow: `inset 0 0 0 1px ${c.ring}` }}
      data-testid="place-tile-art"
      data-kind={kind}
    >
      <Glyph className={iconClassName} />
    </span>
  );
}

/**
 * The drawn tile with the photo over it. `src` undefined = still looking
 * (the tile breathes); null/'' = no photo (the tile alone).
 */
export function PlaceImage({
  src,
  kind,
  className = '',
  iconClassName,
  imgClassName = '',
  title,
  loading = 'lazy',
  onFail,
}: {
  src: string | null | undefined;
  kind: PlaceKind;
  className?: string;
  iconClassName?: string;
  imgClassName?: string;
  title?: string;
  loading?: 'lazy' | 'eager';
  /** The photo didn't load (or was a tracking pixel): try another source. */
  onFail?: () => void;
}) {
  const [state, setState] = React.useState<'loading' | 'ok' | 'failed'>('loading');
  React.useEffect(() => setState('loading'), [src]);
  const ref = React.useRef<HTMLImageElement>(null);
  // Already decoded (from cache) before React attached onLoad.
  React.useEffect(() => {
    const el = ref.current;
    if (el && el.complete) setState(el.naturalWidth > 8 ? 'ok' : 'failed');
  }, [src]);
  const failRef = React.useRef(onFail);
  failRef.current = onFail;
  React.useEffect(() => {
    if (state === 'failed') failRef.current?.();
  }, [state]);
  const looking = src === undefined;
  return (
    <span className={`relative block overflow-hidden ${className}`}>
      <span className={looking ? 'animate-tp-pulse motion-reduce:animate-none' : ''}>
        <PlaceTileArt kind={kind} iconClassName={iconClassName} />
      </span>
      {src && state !== 'failed' && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={ref}
          src={src}
          alt=""
          title={title}
          loading={loading}
          decoding="async"
          draggable={false}
          onLoad={(e) => setState(e.currentTarget.naturalWidth > 8 ? 'ok' : 'failed')}
          onError={() => setState('failed')}
          className={`absolute inset-0 block h-full w-full object-cover transition-opacity duration-220 ease-tp-out motion-reduce:transition-none ${
            state === 'ok' ? 'opacity-100' : 'opacity-0'
          } ${imgClassName}`}
        />
      )}
    </span>
  );
}
